import { FlaskConical, Radio } from 'lucide-react';
import { Switch } from '../../../components/ui/switch.js';
import { useDemoMode } from '../context.js';

interface DemoModeToggleProps {
  /** Lay out the toggle as an inline row (used in the user-settings
   *  page) vs. a compact pill (used in the dashboard header). */
  variant?: 'row' | 'pill';
}

/**
 * Operator-facing switch for demo mode. Sits in user settings as a
 * full row; embeddable in any header as a compact pill. The shared
 * `useDemoMode` hook tracks the persistent state — flipping this
 * switch propagates through every hook that uses `useDemoOrLive`.
 *
 * Replaces the legacy `wf-audit/.../DemoModeToggle.tsx` (32 LOC)
 * with a Radix-styled equivalent. The legacy reloaded the page on
 * flip; the new version doesn't — TanStack Query re-runs every
 * `useDemoOrLive` consumer on the next render, so there's no need
 * to drop in-memory caches.
 */
export function DemoModeToggle({ variant = 'row' }: DemoModeToggleProps): JSX.Element {
  const { isDemoMode, toggleDemoMode } = useDemoMode();

  if (variant === 'pill') {
    return (
      <div className="flex items-center gap-2 rounded-full border border-foreground/15 bg-foreground/5 px-3 py-1.5 text-xs text-foreground/85">
        {isDemoMode ? (
          <FlaskConical className="h-3.5 w-3.5 text-amber-700 dark:text-amber-300" />
        ) : (
          <Radio className="h-3.5 w-3.5 text-emerald-700 dark:text-emerald-300" />
        )}
        <span className="font-medium">{isDemoMode ? 'Demo' : 'Live'}</span>
        <Switch
          checked={isDemoMode}
          onCheckedChange={toggleDemoMode}
          aria-label="Toggle demo mode"
        />
      </div>
    );
  }

  return (
    <div className="flex items-start justify-between gap-4 rounded-lg border border-foreground/10 bg-foreground/[0.03] p-4">
      <div className="flex items-start gap-3">
        {isDemoMode ? (
          <FlaskConical className="mt-0.5 h-5 w-5 text-amber-700 dark:text-amber-300" />
        ) : (
          <Radio className="mt-0.5 h-5 w-5 text-emerald-700 dark:text-emerald-300" />
        )}
        <div>
          <div className="font-medium text-foreground">Demo mode {isDemoMode ? 'on' : 'off'}</div>
          <p className="mt-1 text-sm text-foreground/60">
            When on, dashboards / scenarios / KPIs render bundled sample data instead of calling the
            engine. Useful for sales demos and net-new-tenant onboarding before the first scenario
            sync. Setting persists across reloads on this device.
          </p>
        </div>
      </div>
      <Switch
        id="demo-mode-toggle"
        checked={isDemoMode}
        onCheckedChange={toggleDemoMode}
        aria-label="Toggle demo mode"
      />
    </div>
  );
}
